import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ClipboardList, FilePlus2, FileText, Pill, Plus, Search, Share2, Trash2, Upload } from "lucide-react";
import { useBotWorkspace } from "../../../components/admin/BotWorkspace";
import {
  createPrescription, createPrescriptionTemplate, finalizePrescription,
  addPrescriptionAttachmentLink, archivePrescriptionTemplate, duplicatePrescriptionTemplate,
  duplicatePrescription, getPrescription, getPrescriptionAttachment, getPrescriptionPdf, listPrescriptionTemplates,
  listPrescriptions, publishPrescriptionTemplate, savePrescriptionCatalogItem,
  searchPrescriptionCatalog, sharePrescription, updatePrescription, updatePrescriptionTemplate,
  uploadPrescriptionAttachment, uploadPrescriptionTemplateLogo,
} from "../../../api/prescriptionApi";
import { getCustomer, getSchedulingErrorMessage, listProviders, searchCustomers } from "../../../api/schedulingApi";
import useAuth from "../../../hooks/useAuth";

const sections = [
  ["chief_complaints", "Chief complaints"],
  ["clinical_observations", "Clinical observations"],
  ["diagnosis", "Diagnosis"],
  ["medicines", "Medicines"],
  ["investigations", "Investigations / tests"],
  ["advice", "Advice / suggestions"],
  ["instructions", "Instructions"],
  ["follow_up_date", "Follow-up date"],
  ["attachments", "Attachments"],
];
const emptyMedicine = () => ({
  medicine_name: "", generic_name: "", strength: "", dosage: "", route: "",
  frequency: "", duration: "", quantity: "", instructions: "",
});
const fromLines = (value) => (value || []).join("\n");
const inputClass = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-100";
const labelClass = "mb-1 block text-xs font-semibold text-slate-600";

function MedicineRow({ botId, value, onChange, onRemove, readOnly }) {
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const search = async (term) => {
    if (!term.trim()) {
      setSuggestions([]);
      return;
    }
    try {
      setSuggestions(await searchPrescriptionCatalog(botId, "medicine", term));
    } catch (err) {
      onChange({ ...value, _lookupError: getSchedulingErrorMessage(err, "Could not search medicine catalog.") });
    }
  };
  const saveFavorite = async () => {
    if (!value.medicine_name.trim()) return;
    setSaving(true);
    try {
      await savePrescriptionCatalogItem(botId, {
        catalog_type: "medicine",
        label: value.medicine_name,
        details: { generic_name: value.generic_name, strength: value.strength },
        shared: false,
      });
    } catch (err) {
      onChange({ ...value, _favoriteError: getSchedulingErrorMessage(err, "Could not save medicine favorite.") });
    } finally {
      setSaving(false);
    }
  };
  const setField = (field) => (event) => onChange({ ...value, [field]: event.target.value });
  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Pill size={16} />Medicine</h3>
        {!readOnly && <button type="button" onClick={onRemove} aria-label="Remove medicine" className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-700"><Trash2 size={16} /></button>}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label><span className={labelClass}>Medicine name</span><input className={inputClass} list={`medicine-catalog-${value._key}`} value={value.medicine_name} disabled={readOnly} onChange={(event) => { setField("medicine_name")(event); search(event.target.value); }} placeholder="Search or type a medicine" required /><datalist id={`medicine-catalog-${value._key}`}>{suggestions.map((item) => <option key={item.id} value={item.label} />)}</datalist></label>
        <label><span className={labelClass}>Generic name</span><input className={inputClass} value={value.generic_name || ""} disabled={readOnly} onChange={setField("generic_name")} /></label>
        <label><span className={labelClass}>Strength</span><input className={inputClass} value={value.strength || ""} disabled={readOnly} onChange={setField("strength")} placeholder="e.g. 500 mg" /></label>
        <label><span className={labelClass}>Dosage</span><input className={inputClass} value={value.dosage || ""} disabled={readOnly} onChange={setField("dosage")} placeholder="e.g. 1 tablet" /></label>
        <label><span className={labelClass}>Route</span><input className={inputClass} value={value.route || ""} disabled={readOnly} onChange={setField("route")} placeholder="e.g. Oral" /></label>
        <label><span className={labelClass}>Frequency</span><input className={inputClass} value={value.frequency || ""} disabled={readOnly} onChange={setField("frequency")} placeholder="e.g. Twice daily" /></label>
        <label><span className={labelClass}>Duration</span><input className={inputClass} value={value.duration || ""} disabled={readOnly} onChange={setField("duration")} placeholder="e.g. 5 days" /></label>
        <label><span className={labelClass}>Quantity</span><input className={inputClass} value={value.quantity || ""} disabled={readOnly} onChange={setField("quantity")} /></label>
        <label className="sm:col-span-2 lg:col-span-1"><span className={labelClass}>Additional instructions</span><input className={inputClass} value={value.instructions || ""} disabled={readOnly} onChange={setField("instructions")} placeholder="Take after meals" /></label>
      </div>
      {!readOnly && <button type="button" disabled={saving || !value.medicine_name.trim()} onClick={saveFavorite} className="mt-3 text-xs font-semibold text-cyan-800 hover:underline">{saving ? "Saving…" : "Save medicine to my favorites"}</button>}
      {(value._lookupError || value._favoriteError) && <p role="alert" className="mt-2 text-xs text-rose-700">{value._lookupError || value._favoriteError}</p>}
    </article>
  );
}

function CatalogText({ botId, type, label, values = [], onChange, disabled }) {
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const search = async (value) => {
    setQuery(value);
    if (!value.trim()) {
      setMatches([]);
      return;
    }
    try {
      setMatches(await searchPrescriptionCatalog(botId, type, value));
    } catch (err) {
      setMatches([]);
      setSearchError(getSchedulingErrorMessage(err, "Could not search suggestion catalog."));
    }
  };
  const [searchError, setSearchError] = useState("");
  const add = async () => {
    const value = query.trim();
    if (!value) return;
    onChange([...values, value]);
    setQuery("");
    setMatches([]);
    setSearchError("");
    try {
      await savePrescriptionCatalogItem(botId, { catalog_type: type, label: value, shared: false });
    } catch (err) {
      setSearchError(getSchedulingErrorMessage(err, "The text was added but could not be saved to your favorites."));
    }
  };
  return (
    <div>
      <span className={labelClass}>{label}</span>
      {!disabled && <div className="flex gap-2"><input className={inputClass} list={`catalog-${type}`} value={query} onChange={(event) => search(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); add(); } }} placeholder="Type or choose a saved suggestion" /><datalist id={`catalog-${type}`}>{matches.map((item) => <option key={item.id} value={item.label} />)}</datalist><button type="button" onClick={add} disabled={!query.trim()} className="rounded-lg border border-slate-300 px-3 text-sm font-semibold text-slate-700">Add</button></div>}
      {!!values.length && <ul className="mt-2 space-y-1">{values.map((value, index) => <li key={`${value}-${index}`} className="flex items-start justify-between gap-2 rounded bg-slate-50 px-2 py-1.5 text-sm text-slate-700"><span>{value}</span>{!disabled && <button type="button" onClick={() => onChange(values.filter((_, position) => position !== index))} aria-label={`Remove ${label} item`} className="text-slate-400 hover:text-rose-700"><Trash2 size={14} /></button>}</li>)}</ul>}
      {searchError && <p role="alert" className="mt-2 text-xs text-rose-700">{searchError}</p>}
    </div>
  );
}

function TemplateDesigner({ botId, providers }) {
  const [templates, setTemplates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [specialty, setSpecialty] = useState("General");
  const [clinicName, setClinicName] = useState("");
  const [clinicAddress, setClinicAddress] = useState("");
  const [clinicPhone, setClinicPhone] = useState("");
  const [doctorName, setDoctorName] = useState("");
  const [qualifications, setQualifications] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [assignedProviderId, setAssignedProviderId] = useState("");
  const [footer, setFooter] = useState("");
  const [enabled, setEnabled] = useState(sections.map(([id]) => id));
  const [logoFiles, setLogoFiles] = useState({});
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);
  const load = useCallback(async () => setTemplates(await listPrescriptionTemplates(botId)), [botId]);
  useEffect(() => { load().catch((err) => setError(getSchedulingErrorMessage(err, "Could not load templates."))); }, [load]);
  const save = async (event) => {
    event.preventDefault();
    setError("");
    setWorking(true);
    try {
      const payload = {
        name,
        specialty,
        assigned_provider_id: assignedProviderId ? Number(assignedProviderId) : null,
        is_default: editingId ? templates.find((item) => item.id === editingId)?.is_default || false : templates.length === 0,
        definition: {
          branding: {
            clinic_name: clinicName, clinic_address: clinicAddress, phone: clinicPhone,
            doctor_name: doctorName, qualifications, registration_number: registrationNumber, footer,
          },
          layout: { margin_mm: 18 },
          sections: enabled,
        },
      };
      if (editingId) await updatePrescriptionTemplate(botId, editingId, payload);
      else await createPrescriptionTemplate(botId, payload);
      setEditingId(null);
      setName("");
      await load();
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not save this template."));
    } finally {
      setWorking(false);
    }
  };
  const publish = async (template) => {
    try {
      await publishPrescriptionTemplate(botId, template.id);
      await load();
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not publish this template."));
    }
  };
  const duplicate = async (template) => {
    try {
      await duplicatePrescriptionTemplate(botId, template.id);
      await load();
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not duplicate this template."));
    }
  };
  const archive = async (template) => {
    try {
      await archivePrescriptionTemplate(botId, template.id);
      await load();
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not archive this template."));
    }
  };
  const uploadLogo = async (template) => {
    const file = logoFiles[template.id];
    if (!file) return;
    try {
      await uploadPrescriptionTemplateLogo(botId, template.id, file);
      setLogoFiles((current) => ({ ...current, [template.id]: null }));
      await load();
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not upload this clinic logo."));
    }
  };
  const edit = (template) => {
    const definition = template.versions?.[0]?.definition || {};
    const branding = definition.branding || {};
    setEditingId(template.id);
    setName(template.name);
    setSpecialty(template.specialty || "General");
    setClinicName(branding.clinic_name || "");
    setClinicAddress(branding.clinic_address || "");
    setClinicPhone(branding.phone || "");
    setDoctorName(branding.doctor_name || "");
    setQualifications(branding.qualifications || "");
    setRegistrationNumber(branding.registration_number || "");
    setAssignedProviderId(template.assigned_provider_id ? String(template.assigned_provider_id) : "");
    setFooter(branding.footer || "");
    setEnabled(definition.sections || sections.map(([id]) => id));
  };
  return (
    <section className="space-y-5">
      <div><h2 className="text-xl font-bold text-slate-900">Prescription templates</h2><p className="mt-1 text-sm text-slate-600">Set clinic branding and the sections shown in the printable A4 prescription. Published versions are preserved for existing prescriptions.</p></div>
      <form onSubmit={save} className="grid gap-4 rounded-xl border border-slate-200 bg-white p-5 md:grid-cols-2">
        <label><span className={labelClass}>Template name</span><input className={inputClass} required value={name} onChange={(event) => setName(event.target.value)} placeholder="Clinic prescription" /></label>
        <label><span className={labelClass}>Specialty</span><input className={inputClass} value={specialty} onChange={(event) => setSpecialty(event.target.value)} /></label>
        <label><span className={labelClass}>Clinic name</span><input className={inputClass} value={clinicName} onChange={(event) => setClinicName(event.target.value)} /></label>
        <label><span className={labelClass}>Doctor name</span><input className={inputClass} value={doctorName} onChange={(event) => setDoctorName(event.target.value)} /></label>
        <label><span className={labelClass}>Clinic address</span><input className={inputClass} value={clinicAddress} onChange={(event) => setClinicAddress(event.target.value)} /></label>
        <label><span className={labelClass}>Clinic phone</span><input className={inputClass} value={clinicPhone} onChange={(event) => setClinicPhone(event.target.value)} /></label>
        <label><span className={labelClass}>Doctor qualifications</span><input className={inputClass} value={qualifications} onChange={(event) => setQualifications(event.target.value)} /></label>
        <label><span className={labelClass}>Registration number</span><input className={inputClass} value={registrationNumber} onChange={(event) => setRegistrationNumber(event.target.value)} /></label>
        <label><span className={labelClass}>Assign to doctor</span><select className={inputClass} value={assignedProviderId} onChange={(event) => setAssignedProviderId(event.target.value)}><option value="">All doctors in clinic</option>{providers.map((provider) => <option key={provider.id} value={provider.id}>{provider.name}</option>)}</select></label>
        <label className="md:col-span-2"><span className={labelClass}>Footer</span><input className={inputClass} value={footer} onChange={(event) => setFooter(event.target.value)} /></label>
        <fieldset className="md:col-span-2"><legend className={labelClass}>Visible sections</legend><div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">{sections.map(([id, title]) => <label key={id} className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={enabled.includes(id)} onChange={(event) => setEnabled((current) => event.target.checked ? [...current, id] : current.filter((value) => value !== id))} />{title}</label>)}</div></fieldset>
        <div className="flex gap-2"><button disabled={working} className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-cyan-900 disabled:opacity-60"><Plus size={16} />{working ? "Saving…" : editingId ? "Save new draft version" : "Create draft template"}</button>{editingId && <button type="button" onClick={() => setEditingId(null)} className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700">Cancel</button>}</div>
      </form>
      {error && <p role="alert" className="rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}
      <div className="space-y-3">{templates.map((template) => <article key={template.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <div><p className="font-semibold text-slate-900">{template.name}{template.is_default ? <span className="ml-2 rounded-full bg-cyan-50 px-2 py-1 text-xs text-cyan-800">Default</span> : null}</p><p className="mt-1 text-xs text-slate-500">{template.specialty} · v{template.versions?.[0]?.version || 1} · {template.status}{template.versions?.[0]?.definition?.branding?.logo_file_id ? " · logo configured" : ""}</p></div>
        <div className="flex flex-wrap gap-2"><label className="flex items-center gap-2 rounded-lg border border-slate-300 px-2 py-1 text-xs text-slate-600"><span>Logo</span><input type="file" accept="image/png,image/jpeg" onChange={(event) => setLogoFiles((current) => ({ ...current, [template.id]: event.target.files?.[0] || null }))} /></label>{logoFiles[template.id] && <button type="button" onClick={() => uploadLogo(template)} className="rounded-lg border border-cyan-200 px-3 py-2 text-sm font-semibold text-cyan-900">Upload logo</button>}<button type="button" onClick={() => edit(template)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700">Edit</button>{template.status !== "published" && <button type="button" onClick={() => publish(template)} className="rounded-lg border border-cyan-200 px-3 py-2 text-sm font-semibold text-cyan-900 hover:bg-cyan-50">Publish</button>}<button type="button" onClick={() => duplicate(template)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700">Duplicate</button><button type="button" onClick={() => archive(template)} className="rounded-lg border border-rose-200 px-3 py-2 text-sm font-semibold text-rose-700">Archive</button></div>
      </article>)}</div>
    </section>
  );
}

export default function PrescriptionsPage() {
  const { businessId: botId, customerId, prescriptionId } = useParams();
  const workspace = useBotWorkspace();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [section, setSection] = useState("history");
  const [customer, setCustomer] = useState(null);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const [providers, setProviders] = useState([]);
  const [providerId, setProviderId] = useState("");
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState(null);
  const [medicines, setMedicines] = useState([]);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [externalUrl, setExternalUrl] = useState("");
  const [includeInPdf, setIncludeInPdf] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [channel, setChannel] = useState("whatsapp");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const activeBot = workspace?.name || workspace?.slug || botId;
  const canManageTemplates = can("prescription_templates.manage", workspace?.client_id);
  const canCreatePrescription = can("prescriptions.create", workspace?.client_id);
  const canSharePrescription = can("prescriptions.share", workspace?.client_id);
  const editingId = prescriptionId ? Number(prescriptionId) : null;
  const readOnly = draft?.status !== "draft";
  const visibleSections = new Set(draft?.template?.sections || sections.map(([id]) => id));
  const hasSection = (name) => visibleSections.has(name);

  const loadPatientData = useCallback(async (selected) => {
    setCustomer(selected);
    setQuery("");
    setMatches([]);
    setDraft(null);
    setMedicines([]);
    setError("");
    if (selected?.phone || selected?.phone_number || selected?.wa_id) {
      setRecipient(selected.phone || selected.phone_number || selected.wa_id);
      setChannel("whatsapp");
    } else if (selected?.email) {
      setRecipient(selected.email);
      setChannel("email");
    }
    if (!selected?.id) return;
    setLoading(true);
    try {
      const rows = await listPrescriptions(botId, { customer_id: selected.id, limit: 100 });
      setHistory(rows || []);
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not load prescription history."));
    } finally {
      setLoading(false);
    }
  }, [botId]);
  useEffect(() => {
    listProviders(botId, { active: true })
      .then((providerRows) => {
        setProviders(providerRows || []);
      })
      .catch((err) => setError(getSchedulingErrorMessage(err, "Could not load clinic configuration.")));
  }, [botId]);
  useEffect(() => {
    if (!customerId) {
      setCustomer(null);
      return;
    }
    getCustomer(botId, customerId)
      .then((found) => loadPatientData(found))
      .catch((err) => setError(getSchedulingErrorMessage(err, "Could not open this patient.")));
  }, [botId, customerId, loadPatientData]);
  useEffect(() => {
    if (!editingId || !customerId) return;
    getPrescription(botId, editingId)
      .then((row) => {
        if (row.customer_id !== customerId) throw new Error("Prescription does not belong to this patient.");
        setDraft(row);
        setMedicines((row.medicines || []).map((medicine) => ({ ...emptyMedicine(), ...medicine, _key: crypto.randomUUID() })));
        setProviderId(row.provider_id ? String(row.provider_id) : "");
      })
      .catch((err) => setError(getSchedulingErrorMessage(err, "Could not open this prescription.")));
  }, [botId, customerId, editingId]);
  const searchPatient = async (event) => {
    event.preventDefault();
    setError("");
    try {
      setMatches(await searchCustomers(botId, { q: query.trim(), limit: 20 }));
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not search patients."));
    }
  };
  const newDraft = async () => {
    setError("");
    setNotice("");
    setLoading(true);
    try {
      const result = await createPrescription(botId, {
        customer_id: customer.id,
        provider_id: providerId ? Number(providerId) : null,
        content: {},
        medicines: [],
      });
      setDraft(result);
      setMedicines([]);
      navigate(`/agents/${encodeURIComponent(botId)}/prescriptions/${encodeURIComponent(customer.id)}/${result.id}`);
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not create prescription draft."));
    } finally {
      setLoading(false);
    }
  };
  const saveDraft = async () => {
    if (!draft) return false;
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const content = {
        chief_complaints: draft.content?.chief_complaints || "",
        clinical_observations: draft.content?.clinical_observations || "",
        diagnosis: draft.content?.diagnosis || "",
        investigations: draft.content?.investigations || [],
        advice: draft.content?.advice || [],
        instructions: draft.content?.instructions || [],
        follow_up_date: draft.content?.follow_up_date || null,
      };
      const result = await updatePrescription(botId, draft.id, {
        content,
        medicines: medicines.map(({ _key, ...medicine }) => medicine).filter((medicine) => medicine.medicine_name.trim()),
      });
      setDraft(result);
      setMedicines((result.medicines || []).map((medicine) => ({ ...emptyMedicine(), ...medicine, _key: crypto.randomUUID() })));
      setNotice("Draft saved.");
      setHistory(await listPrescriptions(botId, { customer_id: customer.id, limit: 100 }));
      return true;
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not save draft."));
      return false;
    } finally {
      setLoading(false);
    }
  };
  const finalize = async () => {
    if (!await saveDraft()) return;
    setLoading(true);
    setError("");
    try {
      const result = await finalizePrescription(botId, draft.id);
      setDraft(result.prescription);
      setNotice("Prescription finalized. It is now locked and ready to share.");
      if (result.pdf_url) window.open(result.pdf_url, "_blank", "noopener,noreferrer");
      setHistory(await listPrescriptions(botId, { customer_id: customer.id, limit: 100 }));
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not finalize prescription."));
    } finally {
      setLoading(false);
    }
  };
  const uploadAttachment = async () => {
    if (!draft || !attachmentFile) return;
    setLoading(true);
    setError("");
    try {
      await uploadPrescriptionAttachment(botId, draft.id, attachmentFile, includeInPdf);
      setAttachmentFile(null);
      setNotice("Attachment added.");
      setDraft(await getPrescription(botId, draft.id));
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not upload attachment."));
    } finally {
      setLoading(false);
    }
  };
  const addLink = async () => {
    if (!draft || !externalUrl.trim()) return;
    setLoading(true);
    setError("");
    try {
      const url = new URL(externalUrl);
      if (url.protocol !== "https:") throw new Error("Use a secure https:// link.");
      await addPrescriptionAttachmentLink(botId, draft.id, {
        url: externalUrl, display_name: url.hostname, include_in_pdf: includeInPdf,
      });
      setExternalUrl("");
      setDraft(await getPrescription(botId, draft.id));
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not add report link."));
    } finally {
      setLoading(false);
    }
  };
  const openAttachment = async (attachment) => {
    try {
      const response = await getPrescriptionAttachment(botId, draft.id, attachment.id);
      window.open(response.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not open attachment."));
    }
  };
  const openPdf = async () => {
    try {
      const response = await getPrescriptionPdf(botId, draft.id);
      window.open(response.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not open PDF."));
    }
  };
  const duplicateDraft = async () => {
    if (!draft) return;
    setLoading(true);
    setError("");
    try {
      const copy = await duplicatePrescription(botId, draft.id);
      setDraft(copy);
      setMedicines((copy.medicines || []).map((medicine) => ({ ...emptyMedicine(), ...medicine, _key: crypto.randomUUID() })));
      navigate(`/agents/${encodeURIComponent(botId)}/prescriptions/${encodeURIComponent(customer.id)}/${copy.id}`);
      setNotice(`Created ${copy.prescription_number} from ${draft.prescription_number}.`);
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not duplicate this prescription."));
    } finally {
      setLoading(false);
    }
  };
  const share = async () => {
    if (!draft || !recipient.trim()) return;
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const result = await sharePrescription(botId, draft.id, {
        channel, recipient: recipient.trim(), idempotency_key: crypto.randomUUID(),
      });
      setNotice(`Prescription sent by ${result.channel}.`);
      setDraft(await getPrescription(botId, draft.id));
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not share prescription."));
    } finally {
      setLoading(false);
    }
  };
  const updateContent = (key, value) => setDraft((current) => ({ ...current, content: { ...current.content, [key]: value } }));
  const updateMedicine = (index, value) => setMedicines((current) => current.map((item, position) => position === index ? value : item));
  const displayedMedicines = useMemo(() => medicines.filter((item) => item.medicine_name), [medicines]);
  const prescriptionBrand = draft?.template?.branding || {};
  const prescriptionDoctor = providers.find((provider) => String(provider.id) === String(draft?.provider_id));
  const prescriptionDoctorName = prescriptionBrand.doctor_name || prescriptionDoctor?.name || "";
  const prescriptionDoctorTitle = prescriptionBrand.qualifications || prescriptionDoctor?.title || "";
  const prescriptionDate = draft?.created_at ? new Date(draft.created_at).toLocaleDateString() : new Date().toLocaleDateString();

  return (
    <main className="mx-auto w-full max-w-7xl space-y-5 p-4 sm:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3"><div className="rounded-xl bg-cyan-50 p-3 text-cyan-800"><ClipboardList size={23} /></div><div><p className="text-xs font-semibold uppercase tracking-wide text-cyan-800">{activeBot}</p><h1 className="text-2xl font-bold text-slate-950">Prescriptions</h1></div></div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setSection("history")} className={`rounded-lg px-3 py-2 text-sm font-semibold ${section === "history" ? "bg-cyan-800 text-white" : "border border-slate-300 text-slate-700"}`}>Patient history</button>
          {canManageTemplates && <button type="button" onClick={() => setSection("templates")} className={`rounded-lg px-3 py-2 text-sm font-semibold ${section === "templates" ? "bg-cyan-800 text-white" : "border border-slate-300 text-slate-700"}`}>Templates</button>}
        </div>
      </header>
      {error && <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}
      {notice && <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">{notice}</p>}
      {section === "templates" ? <TemplateDesigner botId={botId} providers={providers} /> : (
        <>
          {!customer ? (
            <section className="rounded-xl border border-slate-200 bg-white p-5">
              <h2 className="text-lg font-bold text-slate-900">Find a patient</h2>
              <form onSubmit={searchPatient} className="mt-3 flex flex-wrap gap-2">
                <input className={`${inputClass} min-w-64 flex-1`} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by name, phone or patient ID" />
                <button className="inline-flex items-center gap-2 rounded-lg bg-cyan-800 px-4 py-2 text-sm font-semibold text-white"><Search size={16} />Search</button>
              </form>
              <ul className="mt-4 divide-y divide-slate-100">{matches.map((patient) => <li key={patient.id} className="flex flex-wrap items-center justify-between gap-3 py-3"><div><p className="font-semibold text-slate-900">{patient.name || "Unnamed patient"}</p><p className="text-xs text-slate-500">{patient.phone || patient.phone_number || patient.wa_id || "No phone"} · ID {patient.id}</p></div><button type="button" onClick={() => { loadPatientData(patient); navigate(`/agents/${encodeURIComponent(botId)}/prescriptions/${encodeURIComponent(patient.id)}`); }} className="rounded-lg border border-cyan-200 px-3 py-2 text-sm font-semibold text-cyan-900">Open history</button></li>)}</ul>
            </section>
          ) : !draft ? (
            <section className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-5">
                <div><button type="button" onClick={() => navigate(`/agents/${encodeURIComponent(botId)}/prescriptions`)} className="mb-2 inline-flex items-center gap-1 text-xs font-semibold text-cyan-800"><ArrowLeft size={14} />Find another patient</button><h2 className="text-xl font-bold text-slate-900">{customer.name || "Patient"} <span className="ml-1 text-sm font-normal text-slate-500">· {customer.phone || customer.phone_number || customer.wa_id || "No phone"}</span></h2><p className="mt-1 text-xs text-slate-500">Patient ID {customer.id} · {customer.gender || "Gender not recorded"}{customer.date_of_birth ? ` · DOB ${customer.date_of_birth}` : ""}</p></div>
                <div className="flex flex-wrap items-end gap-2"><label><span className={labelClass}>Doctor</span><select className={inputClass} value={providerId} onChange={(event) => setProviderId(event.target.value)}><option value="">Select doctor (optional)</option>{providers.map((provider) => <option key={provider.id} value={provider.id}>{provider.name}</option>)}</select></label>{canCreatePrescription && <button type="button" disabled={loading} onClick={newDraft} className="inline-flex items-center gap-2 rounded-lg bg-cyan-800 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60"><FilePlus2 size={16} />Create prescription</button>}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white">
                <div className="border-b border-slate-100 px-5 py-4"><h3 className="font-bold text-slate-900">Prescription history</h3></div>
                {!history.length ? <p className="p-5 text-sm text-slate-500">No prescriptions for this patient yet.</p> : <ul className="divide-y divide-slate-100">{history.map((item) => <li key={item.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4"><div><p className="font-semibold text-slate-900">{item.prescription_number} <span className="ml-2 rounded-full bg-slate-100 px-2 py-1 text-xs capitalize text-slate-600">{item.status}</span></p><p className="mt-1 text-xs text-slate-500">{item.created_at ? new Date(item.created_at).toLocaleString() : ""} · {item.medicines?.length || 0} medicines</p></div><button type="button" onClick={() => { setDraft(item); setMedicines((item.medicines || []).map((medicine) => ({ ...emptyMedicine(), ...medicine, _key: crypto.randomUUID() }))); navigate(`/agents/${encodeURIComponent(botId)}/prescriptions/${encodeURIComponent(customer.id)}/${item.id}`); }} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700">Open</button></li>)}</ul>}
              </div>
            </section>
          ) : (
            <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1.6fr)_minmax(320px,0.8fr)]">
              <section className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
                  <div><button type="button" onClick={() => navigate(`/agents/${encodeURIComponent(botId)}/prescriptions/${encodeURIComponent(customer.id)}`)} className="mb-1 inline-flex items-center gap-1 text-xs font-semibold text-cyan-800"><ArrowLeft size={14} />Patient history</button><h2 className="text-lg font-bold text-slate-900">{draft.prescription_number || "New prescription"}{readOnly && <span className="ml-2 rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-800">{draft.status}</span>}</h2><p className="text-xs text-slate-500">{customer.name || draft.patient?.name} · {customer.phone || customer.phone_number || customer.wa_id || "No phone"}</p></div>
                  {readOnly && <div className="flex flex-wrap gap-2">{canCreatePrescription && <button type="button" onClick={duplicateDraft} disabled={loading} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700">Duplicate as draft</button>}<button type="button" onClick={openPdf} className="inline-flex items-center gap-2 rounded-lg border border-cyan-200 px-3 py-2 text-sm font-semibold text-cyan-900"><FileText size={16} />Preview PDF</button></div>}
                </div>
                {(hasSection("chief_complaints") || hasSection("clinical_observations") || hasSection("diagnosis")) && <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-slate-700">Clinical notes</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {hasSection("chief_complaints") && <label className="sm:col-span-2"><span className={labelClass}>Chief complaints</span><textarea rows={2} className={inputClass} disabled={readOnly} value={draft.content?.chief_complaints || ""} onChange={(event) => updateContent("chief_complaints", event.target.value)} /></label>}
                    {hasSection("clinical_observations") && <label><span className={labelClass}>Clinical observations</span><textarea rows={3} className={inputClass} disabled={readOnly} value={draft.content?.clinical_observations || ""} onChange={(event) => updateContent("clinical_observations", event.target.value)} /></label>}
                    {hasSection("diagnosis") && <label><span className={labelClass}>Diagnosis</span><textarea rows={3} className={inputClass} disabled={readOnly} value={draft.content?.diagnosis || ""} onChange={(event) => updateContent("diagnosis", event.target.value)} /></label>}
                  </div>
                </div>}
                {hasSection("medicines") && <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between"><h3 className="font-bold text-slate-900">Medicines</h3>{!readOnly && <button type="button" onClick={() => setMedicines((rows) => [...rows, { ...emptyMedicine(), _key: crypto.randomUUID() }])} className="inline-flex items-center gap-1 rounded-lg bg-cyan-800 px-3 py-2 text-sm font-semibold text-white"><Plus size={15} />Add medicine</button>}</div>
                  {!medicines.length && <p className="text-sm text-slate-500">No medicines added.</p>}
                  {medicines.map((medicine, index) => <MedicineRow key={medicine._key} botId={botId} value={medicine} readOnly={readOnly} onChange={(value) => updateMedicine(index, value)} onRemove={() => setMedicines((rows) => rows.filter((_, position) => position !== index))} />)}
                </div>}
                {(hasSection("investigations") || hasSection("advice") || hasSection("instructions") || hasSection("follow_up_date")) && <div className="grid gap-4 rounded-xl border border-slate-200 bg-white p-5 sm:grid-cols-2">
                  {hasSection("investigations") && <CatalogText botId={botId} type="investigation" label="Investigations / tests" disabled={readOnly} values={draft.content?.investigations || []} onChange={(items) => updateContent("investigations", items)} />}
                  {hasSection("advice") && <CatalogText botId={botId} type="advice" label="Advice / suggestions" disabled={readOnly} values={draft.content?.advice || []} onChange={(items) => updateContent("advice", items)} />}
                  {hasSection("instructions") && <CatalogText botId={botId} type="instruction" label="Instructions" disabled={readOnly} values={draft.content?.instructions || []} onChange={(items) => updateContent("instructions", items)} />}
                  {hasSection("follow_up_date") && <label><span className={labelClass}>Follow-up date</span><input type="date" className={inputClass} disabled={readOnly} value={draft.content?.follow_up_date || ""} onChange={(event) => updateContent("follow_up_date", event.target.value || null)} /></label>}
                </div>}
                {hasSection("attachments") && <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-5">
                  <h3 className="font-bold text-slate-900">Attachments and external report links</h3>
                  {!readOnly && <>
                    <div className="flex flex-wrap items-end gap-3"><label className="min-w-52 flex-1"><span className={labelClass}>Choose PDF, JPEG, or PNG</span><input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className={inputClass} onChange={(event) => setAttachmentFile(event.target.files?.[0] || null)} /></label><label className="flex items-center gap-2 pb-2 text-sm text-slate-700"><input type="checkbox" checked={includeInPdf} onChange={(event) => setIncludeInPdf(event.target.checked)} />List in PDF</label><button type="button" disabled={!attachmentFile || loading} onClick={uploadAttachment} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"><Upload size={15} />Upload</button></div>
                    <div className="flex flex-wrap gap-2"><input className={`${inputClass} min-w-52 flex-1`} value={externalUrl} onChange={(event) => setExternalUrl(event.target.value)} placeholder="https:// secure report link" /><button type="button" disabled={!externalUrl.trim() || loading} onClick={addLink} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700">Add link</button></div>
                  </>}
                  {!!draft.attachments?.length && <ul className="space-y-2">{draft.attachments.map((attachment) => <li key={attachment.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm"><span>{attachment.display_name}{attachment.include_in_pdf ? " · listed in PDF" : ""}</span><button type="button" onClick={() => openAttachment(attachment)} className="text-xs font-semibold text-cyan-800">Preview</button></li>)}</ul>}
                </section>}
                <div className="flex flex-wrap gap-2">
                  {!readOnly && <><button type="button" disabled={loading} onClick={saveDraft} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 disabled:opacity-60">Save draft</button><button type="button" disabled={loading} onClick={finalize} className="rounded-lg bg-cyan-800 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60">{loading ? "Saving…" : "Finalize prescription"}</button></>}
                </div>
              </section>
              <aside className="space-y-4 lg:sticky lg:top-4">
                <section className="rounded-xl border border-slate-200 bg-white p-5">
                  <div className="mb-4 flex items-center justify-between"><h3 className="font-bold text-slate-900">Prescription pad preview</h3><span className="text-xs text-slate-500">A4 · auto paginated</span></div>
                  <article className="mx-auto flex aspect-[210/297] w-full max-w-[390px] flex-col overflow-y-auto border border-slate-300 bg-white p-4 text-slate-800 shadow-[0_8px_24px_rgba(15,23,42,0.12)] sm:p-5">
                    <header className="border-b-[3px] border-double border-cyan-800 pb-3 text-center">
                      <p className="text-[7px] font-semibold uppercase tracking-[0.2em] text-cyan-800">Medical prescription</p>
                      <h4 className="mt-1 font-serif text-base font-bold leading-tight text-slate-900 sm:text-lg">{prescriptionBrand.clinic_name || activeBot || "Clinic"}</h4>
                      {(prescriptionBrand.clinic_address || prescriptionBrand.phone) && <p className="mt-1 text-[8px] leading-snug text-slate-600">{[prescriptionBrand.clinic_address, prescriptionBrand.phone].filter(Boolean).join(" · ")}</p>}
                      {(prescriptionDoctorName || prescriptionDoctorTitle || prescriptionBrand.registration_number) && <div className="mt-2 border-t border-cyan-100 pt-2">
                        {prescriptionDoctorName && <p className="font-serif text-[11px] font-bold text-slate-900">Dr. {prescriptionDoctorName.replace(/^Dr\.?\s*/i, "")}</p>}
                        {prescriptionDoctorTitle && <p className="text-[8px] text-slate-600">{prescriptionDoctorTitle}</p>}
                        {prescriptionBrand.registration_number && <p className="text-[7px] text-slate-500">Reg. No. {prescriptionBrand.registration_number}</p>}
                      </div>}
                    </header>

                    <div className="mt-3 flex items-center justify-between gap-2 text-[8px]">
                      <p><span className="font-semibold text-slate-500">No.</span> {draft.prescription_number || "Draft"}</p>
                      <p><span className="font-semibold text-slate-500">Date</span> {prescriptionDate}</p>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 border-y border-slate-200 py-2 text-[8px]">
                      <p className="col-span-2 truncate"><span className="font-semibold text-slate-500">Patient</span> <span className="font-semibold">{customer.name || draft.patient?.name || "Patient"}</span></p>
                      <p><span className="font-semibold text-slate-500">Age / Sex</span> {draft.patient?.age ?? "—"} / {customer.gender || draft.patient?.gender || "—"}</p>
                      <p className="truncate"><span className="font-semibold text-slate-500">Patient ID</span> {customer.id}</p>
                      <p className="col-span-2 truncate"><span className="font-semibold text-slate-500">Contact</span> {customer.phone || customer.phone_number || customer.wa_id || "—"}</p>
                    </div>

                    {hasSection("chief_complaints") && draft.content?.chief_complaints && <div className="mt-2 text-[8px]"><span className="font-semibold text-slate-600">C/O: </span><span className="whitespace-pre-wrap">{draft.content.chief_complaints}</span></div>}
                    {hasSection("clinical_observations") && draft.content?.clinical_observations && <div className="mt-1 text-[8px]"><span className="font-semibold text-slate-600">Examination: </span><span className="whitespace-pre-wrap">{draft.content.clinical_observations}</span></div>}
                    {hasSection("diagnosis") && draft.content?.diagnosis && <div className="mt-1 text-[8px]"><span className="font-semibold text-slate-600">Diagnosis: </span><span className="whitespace-pre-wrap">{draft.content.diagnosis}</span></div>}

                    <div className="mt-3 flex items-center gap-2">
                      <span className="font-serif text-2xl font-bold italic leading-none text-cyan-900">℞</span>
                      <span className="h-px flex-1 bg-cyan-100" />
                    </div>
                    {hasSection("medicines") && <div className="mt-2">
                      {displayedMedicines.map((medicine, index) => <div key={medicine._key} className="grid grid-cols-[18px_1fr] gap-1 border-b border-dotted border-slate-200 py-2 text-[8px] last:border-0">
                        <span className="font-semibold text-cyan-900">{index + 1}.</span>
                        <div>
                          <p className="font-semibold text-slate-900">{medicine.medicine_name} {medicine.strength}</p>
                          <p className="mt-0.5 text-slate-600">{[medicine.dosage, medicine.route, medicine.frequency, medicine.duration].filter(Boolean).join(" · ") || "Dose · frequency · duration"}</p>
                          {medicine.instructions && <p className="mt-0.5 italic text-slate-500">{medicine.instructions}</p>}
                        </div>
                      </div>)}
                      {!displayedMedicines.length && <div className="space-y-4 py-2 text-[8px] text-slate-400"><p>Medicine, dose and instructions</p><div className="h-px bg-slate-100" /><p>Additional prescription</p><div className="h-px bg-slate-100" /></div>}
                    </div>}

                    {hasSection("investigations") && !!draft.content?.investigations?.length && <div className="mt-2 text-[8px]"><span className="font-semibold text-slate-600">Investigations: </span>{fromLines(draft.content.investigations)}</div>}
                    {hasSection("advice") && !!draft.content?.advice?.length && <div className="mt-2 text-[8px]"><span className="font-semibold text-slate-600">Advice: </span>{fromLines(draft.content.advice)}</div>}
                    {hasSection("instructions") && !!draft.content?.instructions?.length && <div className="mt-2 text-[8px]"><span className="font-semibold text-slate-600">Instructions: </span>{fromLines(draft.content.instructions)}</div>}
                    {hasSection("follow_up_date") && draft.content?.follow_up_date && <p className="mt-2 text-[8px]"><span className="font-semibold text-slate-600">Follow-up: </span>{draft.content.follow_up_date}</p>}

                    <footer className="mt-auto flex min-h-16 flex-col justify-end pt-5 text-right">
                      <p className="ml-auto w-28 border-t border-slate-400 pt-1 text-center text-[7px] text-slate-500">Doctor&apos;s signature</p>
                      {prescriptionDoctorName && <p className="mt-1 text-[8px] font-semibold text-slate-700">{prescriptionDoctorName}</p>}
                    </footer>
                    {prescriptionBrand.footer && <p className="mt-2 border-t border-cyan-100 pt-1 text-center text-[7px] text-slate-500">{prescriptionBrand.footer}</p>}
                  </article>
                </section>
                {readOnly && canSharePrescription && <section className="space-y-3 rounded-xl border border-cyan-100 bg-cyan-50 p-5">
                  <h3 className="flex items-center gap-2 font-bold text-cyan-950"><Share2 size={17} />Share prescription</h3>
                  <label><span className={labelClass}>Channel</span><select className={inputClass} value={channel} onChange={(event) => setChannel(event.target.value)}><option value="whatsapp">WhatsApp</option><option value="email">Email</option></select></label>
                  <label><span className={labelClass}>Recipient</span><input className={inputClass} value={recipient} onChange={(event) => setRecipient(event.target.value)} placeholder={channel === "email" ? "patient@example.com" : "Country code and phone"} /></label>
                  <button type="button" disabled={loading || !recipient.trim()} onClick={share} className="w-full rounded-lg bg-cyan-800 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60">{loading ? "Sending…" : `Send by ${channel === "email" ? "email" : "WhatsApp"}`}</button>
                </section>}
              </aside>
            </div>
          )}
        </>
      )}
    </main>
  );
}
